'use client';

import { useMemo } from 'react';
import dynamic from 'next/dynamic';
import type { ApexOptions } from 'apexcharts';
import { useThemeMode } from '@terra/ui-design-system';
import {
  Box,
  css,
  getTerraGraphCategoryColors,
  token,
} from '@terra/ui-styled-system';
import { type LineTooltipParams, formatAxisLabel } from './format';

const ReactApexChart = dynamic(() => import('react-apexcharts'), {
  ssr: false,
});

export interface BubbleChartDataPoint {
  x: number;
  y: number;
  z: number;
  name: string;
  businessUnit?: string;
  details?: {
    criticalFindings: number;
    highFindings: number;
    newSignals: number;
    inTestingSignals: number;
    needsReviewSignals: number;
    testedSignals: number;
    testStatus: string;
  };
}

export interface BubbleChartProps {
  series: {
    name: string;
    data: BubbleChartDataPoint[];
  }[];
  title?: string;
  height?: number | string;
  width?: number | string;
  colors?: string[];
  xaxisTitle?: string;
  yaxisTitle?: string;
  showDataLabels?: boolean;
  tooltipCustom?: (options: LineTooltipParams) => string;
  showLegend?: boolean;
  legendPosition?: 'top' | 'right' | 'bottom' | 'left';
  xaxisMin?: number;
  xaxisMax?: number;
  yaxisMin?: number;
  yaxisMax?: number;
  minBubbleRadius?: number;
  maxBubbleRadius?: number;
}

const bubbleChartClassName = css({
  w: 'full',
  '& .apexcharts-datalabel text, & .apexcharts-data-labels text, & text.apexcharts-datalabel':
    {
      fill: 'fg.default!',
    },
  '& .apexcharts-datalabels rect, & .apexcharts-data-labels rect': {
    fill: 'bg.canvas!',
    stroke: 'border.default!',
    strokeWidth: '1px!',
    opacity: '1!',
  },
});

export function BubbleChart({
  series,
  title = '',
  height = 350,
  width = '100%',
  colors = getTerraGraphCategoryColors(),
  xaxisTitle = '',
  yaxisTitle = '',
  showDataLabels = false,
  tooltipCustom,
  showLegend = true,
  legendPosition = 'bottom',
  xaxisMin,
  xaxisMax,
  yaxisMin,
  yaxisMax,
  minBubbleRadius = 6,
  maxBubbleRadius = 18,
}: BubbleChartProps) {
  const { mode: themeMode } = useThemeMode();
  const isDark = themeMode === 'dark';

  const labelColor = token('colors.fg.default');
  const mutedLabelColor = token('colors.fg.muted');
  const gridColor = token('colors.border.default');
  const chartBackground = token('colors.bg.canvas');
  const dataLabelTextColor = labelColor;

  const options = useMemo<ApexOptions>(
    () => ({
      chart: {
        type: 'bubble',
        toolbar: {
          show: false,
        },
        background: chartBackground,
        foreColor: labelColor,
        fontFamily: 'inherit',
        zoom: {
          enabled: false,
        },
      },
      theme: {
        mode: isDark ? 'dark' : 'light',
      },
      plotOptions: {
        bubble: {
          minBubbleRadius,
          maxBubbleRadius,
          zScaling: true,
        },
      },
      colors: colors,
      dataLabels: {
        enabled: showDataLabels,
        offsetY: -22,
        style: {
          colors: [dataLabelTextColor],
          fontSize: '11px',
          fontFamily: 'inherit',
          fontWeight: 600,
        },
        background: {
          enabled: true,
          foreColor: dataLabelTextColor,
          borderRadius: 4,
          padding: 5,
          opacity: 1,
          borderWidth: 1,
          borderColor: gridColor,
          dropShadow: {
            enabled: false,
          },
        },
        formatter: (
          _val: number,
          opts: {
            w: { config: { series: { data: { name: string }[] }[] } };
            seriesIndex: number;
            dataPointIndex: number;
          },
        ) => {
          const point =
            opts.w.config.series[opts.seriesIndex].data[opts.dataPointIndex];

          return point.name;
        },
      },
      title: {
        text: title,
        align: 'left',
        style: {
          color: labelColor,
          fontFamily: 'inherit',
        },
      },
      xaxis: {
        title: {
          text: xaxisTitle,
          style: {
            color: labelColor,
            fontFamily: 'inherit',
            fontWeight: 600,
          },
        },
        type: 'numeric',
        min: xaxisMin,
        max: xaxisMax,
        labels: {
          formatter: formatAxisLabel,
          style: {
            colors: mutedLabelColor,
            fontFamily: 'inherit',
            fontSize: '12px',
          },
        },
      },
      yaxis: {
        title: {
          text: yaxisTitle,
          style: {
            color: labelColor,
            fontFamily: 'inherit',
            fontWeight: 600,
          },
        },
        min: yaxisMin,
        max: yaxisMax,
        labels: {
          formatter: formatAxisLabel,
          style: {
            colors: mutedLabelColor,
            fontFamily: 'inherit',
            fontSize: '12px',
          },
        },
      },
      tooltip: {
        theme: isDark ? 'dark' : 'light',
        enabled: true,
        ...(tooltipCustom && { custom: tooltipCustom }),
        shared: false,
        intersect: true,
      },
      legend: {
        show: showLegend,
        position: legendPosition,
        labels: {
          colors: labelColor,
        },
        fontFamily: 'inherit',
      },
      fill: {
        opacity: 0.85,
      },
      grid: {
        borderColor: gridColor,
      },
    }),
    [
      colors,
      showDataLabels,
      title,
      xaxisTitle,
      yaxisTitle,
      isDark,
      tooltipCustom,
      showLegend,
      legendPosition,
      xaxisMin,
      xaxisMax,
      yaxisMin,
      yaxisMax,
      minBubbleRadius,
      maxBubbleRadius,
      labelColor,
      mutedLabelColor,
      dataLabelTextColor,
      chartBackground,
      gridColor,
    ],
  );

  return (
    <Box className={bubbleChartClassName} style={{ width }}>
      <ReactApexChart
        options={options}
        series={series}
        type="bubble"
        height={height}
        width={width}
      />
    </Box>
  );
}

export default BubbleChart;
